import { withTimeout } from '@/lib/compat'
import type { ContentCard, ContentDetail } from '@/lib/catalog'
import { withLanguage } from '@/lib/language'
import { readSelectedProfileId } from '@/lib/profiles'
import { authStatus, readSession, sessionGeneration } from '@/lib/session'

/**
 * «Sonra İzlə» — istifadəçinin siyahıya saldığı filmlər (`/api/me/watchlist`).
 *
 * ⚠ NİYƏ ƏLAVƏ OLUNDU (522 pariteti): Android/Apple TV ana ekranı bu sıranı
 *   `HomeViewModel`-də qurur — sıra düzülüşü «Davam et → Sizin üçün seçilənlər →
 *   Sonra İzlə → redaksiya blokları»dır («yarımçıq film ən güclü niyyətdir,
 *   „sonra izlə" isə ən zəifi — hələ başlamayıb»). Veb TV boot-da yalnız ilk
 *   ikisini çəkirdi; uc Android `KinoboxClient.watchlist()` ilə eynidir.
 *
 * ⚠ AKTİV BAXIŞ PROFİLİNƏ bağlıdır — `profileId` məcburi query parametridir
 *   (boş göndərsək 400 alardıq), ona görə profil seçilməyibsə sorğu getmir.
 *
 * ⚠ OXUMA HEÇ VAXT REJECT ETMİR (`continue.ts` ilə eyni qərar): sıra boot
 *   yolundadır (`main.ts`), sorğu sınsa BÜTÜN ekran gecikərdi. Alınmasa boş
 *   massiv qayıdır və sıra sadəcə qurulmur — Android-dəki
 *   `runCatching { client.watchlist() }` davranışının eynisi.
 *
 * ⚠⚠ YAZMA UCLARI 2026-08-30-DA ƏLAVƏ OLUNDU (sahib tələbi: «bütün tv projelərdə
 *    sonra izlə modulunu əlavə et»). 2026-08-04-də düymə BÜTÜN TV səthlərindən
 *    çıxarılmışdı («ümumiyyətlə tv projelərin hamısında sonra izlə buttonunu
 *    yığışdır») — həmin qərar sahibin öz tələbi ilə GERİ QAYTARILDI.
 *
 * ⚠ UC MÜQAVİLƏSİ (`Api/.../Watch/WatchEndpoints.cs`, saytdakı
 *   `apps/web/src/lib/me.ts` ilə eyni): GET → `profileId` + `lang`;
 *   POST → GÖVDƏDƏ `profileId` + `contentId`, `lang` YOXDUR;
 *   DELETE → query-də `profileId` + `contentId`, `lang` YOXDUR.
 *   Uğur cavabı 204-dür (gövdəsiz), ona görə mətn oxunmur.
 */

const TIMEOUT_MS = 6000

/** Serverin `WatchlistItemDto` cavabı (TV-yə lazım olan sahələr). */
interface WatchlistItem {
  contentId: string
  contentTitle: string | null
  slug: string | null
  posterUrl: string | null
  /** Fokus qatının metası — izahı `lib/continue.ts`-dəki eyni bloktadır (2026-08-08). */
  ageLimit?: number | null
  year?: number | null
  rating?: number | null
  durationMinutes?: number | null
  genreNames?: string[]
  moodNames?: string[]
}

/**
 * Yazma cavabının çağırana lazım olan üç halı.
 *
 * ⚠ `signIn` VƏ `error` AYRIDIR — bu, ölçülmüş qərardır (`session.ts` →
 *   `refreshFailure`, 2026-07-31): `authStatus` KEÇİCİ nasazlıqda (Wi-Fi
 *   kəsintisi · 429 · 5xx · timeout) **0** qaytarır, sessiya isə YERİNDƏDİR.
 *   Sıfırı 401 kimi oxusaydıq istifadəçi 2 saniyəlik şəbəkə çuxurundan sonra
 *   QR ekranına atılardı — pultla yenidən cütləşdirmək isə ağır əməliyyatdır.
 */
export type WatchlistResult = 'ok' | 'signIn' | 'error'

export function getWatchlist(): Promise<ContentCard[]> {
  const profileId = readSelectedProfileId()
  if (!readSession() || !profileId) return Promise.resolve<ContentCard[]>([])
  syncMembersProfile()
  const owner = ownerKey()

  const path = withLanguage('/api/me/watchlist?profileId=' + encodeURIComponent(profileId))

  // Sorğudan ƏVVƏLKİ çevirmə sayı — izahı `mutations`-dadır.
  const asked = mutations

  return withTimeout(authStatus(path), TIMEOUT_MS, 'me/watchlist')
    .then((response) => {
      if (owner !== ownerKey()) return []
      if (response.status !== 200 || !response.text) return []
      const parsed = JSON.parse(response.text) as WatchlistItem[]
      // ⚠ ÜZVLÜK İNDEKSİ XAM CAVABDAN QURULUR, aşağıdakı `cards`-dan YOX: kart
      //   siyahısı `slug`-sız sətri atır (açıla bilməyən kart göstərilməməlidir),
      //   halbuki həmin film SERVERDƏ siyahıdadır — indeksi kartlardan qursaydıq
      //   onun modalında «+ Sonra İzlə» yazılar, təkrar əlavə isə serverdən
      //   xəta alardı. Boş cavab da üzvlük cavabıdır: köhnə indeks qalsaydı
      //   telefondan silinən film TV-də «Siyahımdadır» görünməyə davam edərdi.
      // ⚠ SORĞU UÇARKƏN DÜYMƏ BASILIBSA CAVAB KÖHNƏDİR: indeks əvəzlənməz,
      //   yoxsa istifadəçinin öz əlavəsi/çıxarışı yox olur (`mutations` qeydi).
      if (asked === mutations && !hasPendingMutation(owner)) rememberMembers(parsed)
      if (!parsed || parsed.length === 0) return []

      const cards: ContentCard[] = []
      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i]
        // ⚠ `slug` OLMADAN kart açıla bilmir (modal slug ilə işləyir) — belə sətri
        //   göstərmək «basılan, amma heç nə etməyən kart» olardı (continue.ts qaydası).
        if (!item || !item.slug) continue
        cards.push({
          id: item.contentId,
          slug: item.slug,
          title: item.contentTitle || item.slug,
          // ⚠ NÖV cavabda yoxdur (sabit qalır); qalan meta isə VAR və fokus qatına
          //   ötürülür — sahib tələbi 2026-08-08 (Android `HomeViewModel.item(
          //   WatchlistItemDto)` və iOS-dakı eyni map da bu gün düzəldildi).
          kind: 'movie',
          ageLimit: item.ageLimit ? item.ageLimit : null,
          year: item.year ? item.year : null,
          posterUrl: item.posterUrl,
          rating: typeof item.rating === 'number' ? item.rating : null,
          durationMinutes: item.durationMinutes,
          genreNames: item.genreNames,
          moodNames: item.moodNames,
        })
      }
      return cards
    })
    .catch(() => [])
}

/**
 * Siyahıya əlavə edir. 204 = uğur.
 *
 * ⚠ `authStatus` İŞLƏDİLİR, İKİNCİ FETCH ÖRTÜYÜ YAZILMIR (`compat.ts`-in son
 *   şərhi): yalnız o, access token-i lazım olanda yeniləyir, 401-də bir dəfə
 *   təkrarlayır və status kodunu saxlayır.
 * ⚠ GÖVDƏDƏ `lang` YOXDUR — uc onu qəbul etmir (yuxarıdakı müqavilə).
 */
export function addToWatchlist(contentId: string): Promise<WatchlistResult> {
  const profileId = readSelectedProfileId()
  if (!readSession()) return Promise.resolve<WatchlistResult>('signIn')
  if (!profileId || !contentId) return Promise.resolve<WatchlistResult>('error')

  return withTimeout(
    authStatus('/api/me/watchlist', {
      method: 'POST',
      body: JSON.stringify({ profileId: profileId, contentId: contentId }),
    }),
    TIMEOUT_MS,
    'me/watchlist add',
  )
    .then((response) => readResult(response, false))
    .catch((): WatchlistResult => 'error')
}

/** Siyahıdan çıxarır. 204 = uğur. `contentId` QUERY-dədir, gövdə yoxdur. */
export function removeFromWatchlist(contentId: string): Promise<WatchlistResult> {
  const profileId = readSelectedProfileId()
  if (!readSession()) return Promise.resolve<WatchlistResult>('signIn')
  if (!profileId || !contentId) return Promise.resolve<WatchlistResult>('error')

  const path =
    '/api/me/watchlist?profileId=' +
    encodeURIComponent(profileId) +
    '&contentId=' +
    encodeURIComponent(contentId)

  return withTimeout(authStatus(path, { method: 'DELETE' }), TIMEOUT_MS, 'me/watchlist remove')
    .then((response) => readResult(response, true))
    .catch((): WatchlistResult => 'error')
}

/**
 * Status kodunu üç hala yığır.
 *
 * ⚠⚠ 404 YALNIZ SİLMƏDƏ UĞURDUR (ölçüldü 2026-08-30). NƏ ÖLÇÜLDÜ: ucun gövdəsi —
 *    `Api/.../Watch/Commands/WatchlistCommands.cs`. Hər iki əməl İDEMPOTENTDİR:
 *    film artıq siyahıdadırsa POST yenə 204 verir, siyahıda yoxdursa DELETE yenə
 *    204 verir; yəni «vəziyyət onsuz da istədiyin kimidir» halı client-ə 404 kimi
 *    ÜMUMİYYƏTLƏ gəlmir və 409 bu modulda heç vaxt qaytarılmır.
 *    ƏVVƏL NƏ OLURDU: 404 hər iki uc üçün «uğur» sayılırdı. Əlavədə serverin verə
 *    biləcəyi yeganə 404 `Profiles.NotFound`-dur (profil telefondan silinib və ya
 *    seçilmiş id köhnəlib), yəni YAZI BAŞ TUTMAYIB — düymə isə «✓ Siyahımdadır»
 *    yazır, ana ekrana sıra əlavə olunurdu və server heç nə saxlamırdı; istifadəçi
 *    Profil → «Sonra İzlə»də filmi tapmırdı, növbəti açılışda sıradan da itirdi.
 * ⚠ NİYƏ MƏHZ İSTİQAMƏTƏ GÖRƏ: silmədə 404 həqiqətən idempotentdir (siyahı
 *   telefon/saytla paylaşılandır, sətir eyni anda oradan da silinə bilər) — orada
 *   xəta saysaydıq etiket «silindi, amma geri qayıtdı» olardı. Əlavədə isə xəta
 *   saymaq düzgündür: saytdakı `apps/web/src/lib/me.ts` də `response.ok` işlədir.
 */
function readResult(response: { status: number }, removing: boolean): WatchlistResult {
  if (response.status >= 200 && response.status < 300) return 'ok'
  if (removing && response.status === 404) return 'ok'
  if (response.status === 401 || response.status === 403) return 'signIn'
  return 'error'
}

// ── Üzvlük indeksi və dəyişiklik siqnalı ───────────────────────────────────

/**
 * SİYAHIDA OLAN `contentId`-lər.
 *
 * ⚠ NİYƏ AYRICA İNDEKS (kartların massivi deyil): düymə hər açılışda «bu film
 *   siyahıdadırmı» sualına SİNXRON cavab verməlidir — app sıfır-reaktivdir
 *   (PERFORMANCE.md), yəni modal açılandan sonra etiketi dəyişən mexanizm yoxdur
 *   və hər açılışda `/api/me/watchlist`-ə getmək fokus yolunda sorğu deməkdir.
 * ⚠ `Set` DEYİL, adi massiv: siyahı onlarla sətirdir, `indexOf` bəsdir və
 *   köhnə TV mühərriklərində `Set` iterasiyası ilə bağlı sürpriz olmur.
 */
let members: string[] = []

/**
 * İNDEKS HANSI PROFİL ÜÇÜN QURULUB.
 *
 * ⚠⚠ NƏ ÖLÇÜLDÜ (2026-08-30): `members` yalnız `getWatchlist()`-də dolur, o da
 *    BOOT-da və «Sonra İzlə» ekranında çağırılır. Profil dəyişəndə (Profil →
 *    «Profil dəyiş» → `screens/profiles.ts` · brauzerdə `browser/screens/
 *    account.ts`) boot YENİDƏN İŞLƏMİR, çıxışda isə `getWatchlist()` sessiya
 *    yoxdur deyə erkən qayıdır — hər iki halda indeks OLDUĞU KİMİ qalırdı.
 *    ƏVVƏL NƏ OLURDU: A profilinin siyahısındakı film B profilində açılanda düymə
 *    «✓ Siyahımdadır» yazırdı; basılanda B üçün DELETE gedirdi (sətir yoxdur) —
 *    yəni istifadəçi heç vaxt etmədiyi əməliyyatı «geri almağa» bir basış xərcləyir.
 *    Çıxışdan sonra QR ilə bağlanan YENİ hesab da əvvəlkinin üzvlüyünü görürdü.
 * ⚠ NİYƏ MÖHÜR, ÇAĞIRIŞ YERLƏRİNƏ ƏL GƏZDİRMƏK DEYİL: seçim DÖRD yerdə dəyişir
 *   (cihaz profil seçimi · brauzer profil seçimi · çıxış · QR uğuru) və beşincisi
 *   sabah əlavə oluna bilər — biri unudulsa qüsur qayıdır. Möhür modulun
 *   İÇİNDƏDİR, ona görə heç bir yol onu ötə bilmir. Profil id-ləri UUID-dir,
 *   yəni hesablar arasında toqquşma mümkün deyil.
 */
let membersProfileId = ''

function ownerKey(): string {
  const session = readSession()
  return sessionGeneration() + ':' + (session ? session.userId : '') + ':' + (readSelectedProfileId() || '')
}

/** Başqa profilə keçilibsə (və ya çıxış olubsa) indeks «məlum deyil»ə düşür. */
function syncMembersProfile(): void {
  const current = ownerKey()
  if (current === membersProfileId) return
  members = []
  membersProfileId = current
  mutations += 1
}

/**
 * YERLİ ÇEVİRMƏ SAYĞACI — «uçuşdakı cavab köhnədirmi» sualına cavab verir.
 *
 * ⚠ NİYƏ (ölçüldü 2026-08-30): `getWatchlist()` TV-də saniyələr çəkir. Sorğu
 *   uçarkən istifadəçi düyməni basıb filmi siyahıya salsa, sonra gələn KÖHNƏ
 *   cavab həm indeksi, həm də giriş nöqtəsindəki boot keşini əvəzləyib əlavəni
 *   YOX EDİRDİ (düymə «+ Sonra İzlə»yə qayıdır, sıradan kart itir). İstifadəçinin
 *   öz əməli köhnə anlıq şəkildən üstündür.
 */
let mutations = 0

/** Uğurlu çevirmələrin sayı — çağıran sorğudan ƏVVƏL və SONRA müqayisə edir. */
export function watchlistGeneration(): number {
  syncMembersProfile()
  return mutations
}

/** Eyni film üçün ikinci sorğu getməsin (pult/siçan ikiqat basılışı). */
const pending: { [contentId: string]: boolean } = {}

function hasPendingMutation(owner: string): boolean {
  const prefix = owner + ':'
  for (const key in pending) {
    if (pending[key] && key.indexOf(prefix) === 0) return true
  }
  return false
}

/** Dəyişiklik dinləyiciləri — boot keşini sahibi olan modul özü yeniləyir. */
type WatchlistListener = (change: WatchlistChange) => void

/** Uğurlu çevirmədən sonra yayılan siqnal. */
export interface WatchlistChange {
  contentId: string
  /** Ana ekran sırasına qoyulacaq kart — detaldan gələndə də doldurulur. */
  card: ContentCard | null
  added: boolean
}

const listeners: WatchlistListener[] = []

/**
 * Dəyişikliyə abunə olur.
 *
 * ⚠⚠ NİYƏ ABUNƏ, `main.ts`-i İMPORT ETMƏK DEYİL (app.ts → `onRouteChange` və
 *    `setDetailOpener` ilə eyni naxış): siyahının BOOT KEŞİ `main.ts` /
 *    `browser/main.ts`-dədir və ekran fabriki onu `createHomeScreen`-ə ötürür.
 *    Düymə isə modaldadır. Modal `main.ts`-i import etsəydi DÖVRƏ yaranardı
 *    (`main.ts` → `ui/detailModal.ts` → `main.ts`), üstəlik cihaz bundle-ına
 *    brauzer girişi (və əksinə) düşərdi. Abunə ilə asılılıq TƏK İSTİQAMƏTLİDİR:
 *    giriş nöqtəsi bu modula baxır, bu modul heç kimə baxmır.
 * ⚠ ABUNƏ GÖTÜRÜLMÜR: boot-da bir dəfə qurulur və app-la birlikdə yaşayır.
 */
export function onWatchlistChange(listener: WatchlistListener): void {
  listeners.push(listener)
}

/** Film hazırda siyahıdadırmı (indeksdən — şəbəkəyə çıxmır). */
export function isInWatchlist(contentId: string): boolean {
  // Profil dəyişibsə köhnə profilin üzvlüyü ATILIR — izahı `membersProfileId`-dədir.
  syncMembersProfile()
  return Boolean(contentId) && members.indexOf(contentId) >= 0
}

function rememberMembers(items: WatchlistItem[] | null): void {
  const next: string[] = []
  for (let i = 0; items && i < items.length; i++) {
    const item = items[i]
    if (item && item.contentId) next.push(item.contentId)
  }
  members = next
  membersProfileId = ownerKey()
}

function setMember(contentId: string, on: boolean): void {
  const at = members.indexOf(contentId)
  if (on) {
    if (at < 0) members.push(contentId)
    return
  }
  if (at >= 0) members.splice(at, 1)
}

/**
 * DÜYMƏNİN ÇEVRİLMƏSİ — optimistik, sınanda geri qaytarılır.
 *
 * Naxış saytdakı `apps/web/src/components/ContentActions.tsx`-dən götürülüb:
 * vəziyyət DƏRHAL dəyişir, sorğu sonra gedir, cavab mənfidirsə etiket geri qayıdır.
 * Səbəb: TV-də sorğu soyuq başlanğıcda saniyələr çəkir — düymənin cavabını
 * gözlətmək istifadəçidə «basılmadı» hissi yaradır (PERFORMANCE.md §6).
 *
 * ⚠ DÖRD SƏTHDƏ BİR GÖVDƏ (cihaz modalı · cihaz film ekranı · brauzer modalı ·
 *   brauzer film ekranı): çevirmə məntiqi dörd yerə yazılsaydı biri düzəlib
 *   üçü arxada qalardı (rail/nav-dakı eyni səbəb). Səthlər yalnız ÜÇ funksiya
 *   verir: `paint` (etiketi yenidən yaz), `onSignIn` (girişsiz yol) və
 *   `onPickProfile` (sessiya var, profil seçilməyib).
 * ⚠⚠ «PROFİL SEÇİLMƏYİB» «GİRİŞ YOXDUR» DEYİL (2026-08-30). ƏVVƏL tək şərt vardı
 *    (`!readSession() || !readSelectedProfileId()`) və hər dörd səth onu QR
 *    ekranına bağlayırdı — yəni sessiyası SAĞ olan istifadəçi (Profil → «Cihazı
 *    yenidən bağla»dan geri qayıdıb və ya QR uğurundan sonra profil seçimini
 *    yarımçıq buraxıb) düyməni basanda televizoru YENİDƏN CÜTLƏŞDİRMƏYƏ
 *    göndərilirdi. Pultla QR skan etmək ağır əməliyyatdır (yuxarıdakı 56-60
 *    qeydi); doğru yer «Kim baxır?» ekranıdır. `playFilm` də yalnız `!readSession()`
 *    yoxlayır — indi eyni qərar buradadır.
 * ⚠ NİYƏ NAVİQASİYA SƏTHİN İŞİDİR: modalı yalnız onu açan səth bağlaya bilir —
 *   buradan naviqasiya etsək modal profil seçiminin ÜSTÜNDƏ qalar və bütün pult
 *   düymələrini udardı.
 * ⚠ `paint` ARQUMENT ALMIR: çağıran vəziyyəti `isInWatchlist`-dən YENİDƏN oxuyur.
 *   Səbəb ölçülüb: modal PAYLAŞILAN DOM-dur (bir dəfə qurulur) — sorğu uçarkən
 *   istifadəçi başqa filmə keçsə, bayrağı arqumentlə yazsaydıq geri qaytarma
 *   YENİ filmin düyməsini yanlış boyayardı.
 */
export function toggleWatchlist(
  contentId: string,
  card: ContentCard | null,
  paint: () => void,
  onSignIn: () => void,
  onPickProfile: () => void,
): void {
  if (!contentId) return
  if (!readSession()) {
    onSignIn()
    return
  }
  if (!readSelectedProfileId()) {
    onPickProfile()
    return
  }
  const owner = ownerKey()
  const pendingKey = owner + ':' + contentId
  if (pending[pendingKey]) return

  const next = !isInWatchlist(contentId)
  mutations += 1
  pending[pendingKey] = true
  setMember(contentId, next)
  paint()

  const request = next ? addToWatchlist(contentId) : removeFromWatchlist(contentId)
  void request.then((result) => {
    pending[pendingKey] = false
    // 2026-10-04: A profilinin gecikmiş cavabı B indeksini/sırasını dəyişirdi.
    // Yeni profil əvvəlki optimistik əlavənin uğurunu və ya geri alınmasını almır.
    if (owner !== ownerKey()) return
    if (result === 'ok') {
      // Sayğac SİQNALDAN ƏVVƏL artır: dinləyici keşi yeniləyəndə uçuşdakı
      //   `getWatchlist()` artıq «köhnə» sayılmalıdır (`mutations` qeydi).
      mutations += 1
      notify({ contentId: contentId, card: card, added: next })
      return
    }
    // Sınıb — indeks və etiket köhnə hala qayıdır (saytdakı `setInList(!next)`).
    setMember(contentId, !next)
    paint()
    if (result === 'signIn') onSignIn()
  })
}

function notify(change: WatchlistChange): void {
  for (let i = 0; i < listeners.length; i++) {
    const listener = listeners[i]
    if (listener) listener(change)
  }
}

/**
 * Detal cavabından ana ekran sırasına yarayan kart qurur.
 *
 * ⚠ NİYƏ LAZIMDIR: modal kartsız da açıla bilir (`openDetailModal(slug)` —
 *   bərpa yolu, sessiya ekranı). Kart olmasaydı siyahıya yeni əlavə olunan film
 *   ana ekran sırasında yalnız app yenidən açılandan sonra görünərdi.
 * ⚠ `genres` → `genreNames`: adlar eynidir, sahə adı fərqlidir (`lib/catalog.ts`).
 */
export function cardFromDetail(found: ContentDetail): ContentCard {
  return {
    id: found.id,
    slug: found.slug,
    title: found.title,
    kind: found.kind,
    ageLimit: found.ageLimit,
    year: found.year,
    posterUrl: found.posterUrl,
    rating: found.rating,
    durationMinutes: found.durationMinutes,
    genreNames: found.genres,
    backdropUrl: found.backdropUrl,
    isComingSoon: found.isComingSoon,
  }
}
